import { useRef } from "react";
import { useStore } from "../state/store";
import { SHEET_SNAPS, sheetHeight, type SheetSnap } from "../state/layout";
import { motionText, stormIsPotential, systemName } from "../engine/liveSystems";
import { ssCategory } from "./IntensityTrend";

/** The open storm (or what's on screen) in two lines — what the phone's
 *  bottom sheet shows at its lowest. */
function useSummary(): { title: string; tag: string | null; line: string } {
  const mode = useStore((s) => s.mode);
  const live = useStore((s) => s.live);
  const explorer = useStore((s) => s.explorer);
  const focus = useStore((s) => s.focus);

  if (focus.stormId) {
    const name = focus.replay?.name ?? focus.track?.name ?? focus.stormId;
    return { title: `🎯 ${name}`, tag: null, line: "Historical storm · pull up for the detail" };
  }
  if (mode === "explorer") {
    return explorer.replay
      ? { title: `🌀 ${explorer.replay.name}`, tag: null, line: "Replay · pull up for the detail" }
      : { title: "🌀 Explorer", tag: null, line: "Pick a storm from ☰" };
  }
  if (mode === "climatology") return { title: "📊 Climatology", tag: null, line: "Filters and boxes in ☰ · results below" };

  if (live.selectedId) {
    const ref = live.index.find((s) => s.storm_id === live.selectedId) ?? live.demoStorms.find((s) => s.storm_id === live.selectedId);
    const b = live.bundle;
    const last = b?.best_track[b.best_track.length - 1];
    const potential = b ? stormIsPotential(b.storm_id, b.best_track.map((p) => p.storm_type), ref) : false;
    const title = `🌀 ${ref ? systemName(ref) : b?.name ?? live.selectedId}`;
    if (!last) return { title, tag: null, line: live.loading ? "Loading the forecast and guidance…" : "Pull up for the detail" };
    const bits = [`${last.vmax} kt`, last.pmin > 0 ? `${last.pmin} mb` : null, ref ? motionText(ref) : null].filter(Boolean);
    return { title, tag: potential ? "PTC" : ssCategory(last.vmax), line: bits.join(" · ") };
  }
  const areas = (live.genesis?.gtwo ?? []).filter((f) => f.kind === "area").length;
  return {
    title: `🌀 Active systems · ${live.index.length}`,
    tag: null,
    line: `${areas ? `${areas} NHC outlook area${areas === 1 ? "" : "s"} · ` : ""}pull up for the list`,
  };
}

/**
 * The phone's bottom-sheet grip: tap to step peek → half → full, or drag it.
 * Only the grip drags (the panel below scrolls normally); while dragging,
 * the height follows the finger and the sheet snaps to the nearest stop.
 */
export default function SheetHandle({ vh }: { vh: number }) {
  const sheet = useStore((s) => s.ui.sheet);
  const setSheet = useStore((s) => s.setSheet);
  const { title, tag, line } = useSummary();
  const drag = useRef<{ y0: number; h0: number; moved: boolean } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const els = () => {
    const panel = ref.current?.closest(".results-wrap") as HTMLElement | null;
    return { panel, app: panel?.closest(".app") as HTMLElement | null };
  };
  const settle = (snap: SheetSnap) => {
    const { panel, app } = els();
    panel?.classList.remove("dragging");
    app?.style.setProperty("--sheet-h", `${sheetHeight(snap, vh)}px`); // also when the stop doesn't change
    setSheet(snap);
  };
  const step = () => settle(SHEET_SNAPS[(SHEET_SNAPS.indexOf(sheet) + 1) % SHEET_SNAPS.length]);

  return (
    <div
      ref={ref} className="sheet-handle" role="button" tabIndex={0}
      aria-label={`Panel (${sheet}): tap or drag to resize`}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); step(); } }}
      onPointerDown={(e) => {
        const { panel } = els();
        if (!panel) return;
        drag.current = { y0: e.clientY, h0: panel.getBoundingClientRect().height, moved: false };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        const dy = d.y0 - e.clientY;
        if (!d.moved && Math.abs(dy) < 6) return; // a tap, so far
        d.moved = true;
        const { panel, app } = els();
        panel?.classList.add("dragging");
        const h = Math.min(sheetHeight("full", vh), Math.max(sheetHeight("peek", vh), d.h0 + dy));
        app?.style.setProperty("--sheet-h", `${h}px`);
      }}
      onPointerUp={(e) => {
        const d = drag.current;
        drag.current = null;
        if (!d) return;
        if (!d.moved) { step(); return; }
        const h = d.h0 + (d.y0 - e.clientY);
        const nearest = SHEET_SNAPS.reduce((a, b) => (Math.abs(sheetHeight(b, vh) - h) < Math.abs(sheetHeight(a, vh) - h) ? b : a));
        settle(nearest);
      }}
      onPointerCancel={() => { if (drag.current) { drag.current = null; settle(sheet); } }}
    >
      <div className="sheet-grip" />
      <div className="sheet-sum">
        <b>{title}</b>
        {tag && <span className="sheet-tag">{tag}</span>}
      </div>
      <div className="sheet-line">{line}</div>
    </div>
  );
}
